import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ManualAdjustmentDTO, ManualAdjustmentType } from '../../dto/HonorariumLiquidationDTO';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import FieldError from '../FieldError/FieldError';

type FieldName = 'description' | 'amount';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Campo del request → campo del formulario, para los marcadores de VALIDATION_ERROR. */
const REQUEST_FIELDS: Record<string, FieldName> = { description: 'description', amount: 'amount' };

/** Largo de la columna de la descripción del ajuste. */
const DESCRIPTION_MAX_LENGTH = 1000;

const COPY: Record<ManualAdjustmentType, { header: string; action: string; placeholder: string; help: string }> = {
    DEBIT: {
        header: 'Agregar deducción',
        action: 'Agregar deducción',
        placeholder: 'Ej.: 10% de las órdenes facturadas por el profesional',
        help: 'Lo que el profesional le debe a la institución: se resta de lo que cobra.',
    },
    CREDIT: {
        header: 'Agregar crédito',
        action: 'Agregar crédito',
        placeholder: 'Ej.: guardia adicional',
        help: 'Lo que la institución le reconoce al profesional: se suma a lo que cobra.',
    },
};

interface ManualAdjustmentDialogProps {
    visible: boolean;
    type: ManualAdjustmentType;
    onHide: () => void;
    /**
     * Guarda el ajuste (en el backend o en el estado del formulario). Si falla con VALIDATION_ERROR, el diálogo marca
     * los campos; si es otro error de negocio, muestra su mensaje.
     */
    onSubmit: (adjustment: ManualAdjustmentDTO) => Promise<void> | void;
}

/**
 * Débito o crédito manual con descripción y monto, para una liquidación abierta o una rendición que se está por
 * cerrar. El monto siempre es positivo: el tipo dice si resta o suma.
 */
export default function ManualAdjustmentDialog({ visible, type, onHide, onSubmit }: ManualAdjustmentDialogProps) {
    const notify = useNotify();
    const copy = COPY[type];

    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState<number | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const descriptionRef = useRef<HTMLInputElement>(null);
    const amountRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!visible) return;
        setDescription('');
        setAmount(null);
        setErrors({});
    }, [visible]);

    function validate(): FieldErrors {
        const result: FieldErrors = {};
        if (!description.trim()) result.description = 'Ingresá la descripción';
        if (amount == null || amount <= 0) result.amount = 'Ingresá un monto mayor a cero';
        return result;
    }

    function focusFirstError(fieldErrors: FieldErrors) {
        if (fieldErrors.description) descriptionRef.current?.focus();
        else if (fieldErrors.amount) amountRef.current?.focus();
    }

    async function handleSave() {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }

        setSaving(true);
        try {
            await onSubmit({ type, amount: amount!, description: description.trim() });
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            const fieldErrors: FieldErrors = {};
            for (const detail of apiError.details ?? []) {
                const field = REQUEST_FIELDS[detail.field];
                if (field) fieldErrors[field] = detail.message;
            }
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                focusFirstError(fieldErrors);
                return;
            }
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudo agregar. Intentá de nuevo.');
        } finally {
            setSaving(false);
        }
    }

    const idPrefix = `manual-adjustment-${type.toLowerCase()}`;
    const footer = (
        <div className="md-dialog__footer">
            <Button outlined label="Cancelar" className="md-ghost" onClick={onHide} disabled={saving} />
            <Button label={copy.action} className="md-primary" onClick={handleSave} loading={saving} />
        </div>
    );

    return (
        <Dialog
            header={copy.header}
            visible={visible}
            onHide={() => { if (!saving) onHide(); }}
            footer={footer}
            style={{ width: '560px' }}
            breakpoints={{ '640px': '100vw' }}
            className="md-dialog"
            modal
        >
            <div className="liquidation-form">
                <p className="md-help">{copy.help}</p>
                <div className="inputContainer flexible--column">
                    <label htmlFor={`${idPrefix}-description`} className="label inputLabel">Descripción *</label>
                    <InputText
                        id={`${idPrefix}-description`}
                        ref={descriptionRef}
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setErrors(current => ({ ...current, description: undefined }));
                        }}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder={copy.placeholder}
                        className={errors.description ? 'field-error-target' : undefined}
                        aria-invalid={errors.description ? true : undefined}
                        aria-describedby={errors.description ? `${idPrefix}-description-error` : undefined}
                    />
                    <FieldError id={`${idPrefix}-description-error`} message={errors.description} />
                </div>
                <div className="inputContainer flexible--column">
                    <label htmlFor={`${idPrefix}-amount`} className="label inputLabel">Monto *</label>
                    <InputNumber
                        inputId={`${idPrefix}-amount`}
                        inputRef={amountRef}
                        value={amount}
                        onValueChange={(e) => {
                            setAmount(e.value ?? null);
                            setErrors(current => ({ ...current, amount: undefined }));
                        }}
                        mode="currency"
                        currency="ARS"
                        locale="es-AR"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        className={errors.amount ? 'field-error-target' : undefined}
                        aria-invalid={errors.amount ? true : undefined}
                        aria-describedby={errors.amount ? `${idPrefix}-amount-error` : undefined}
                    />
                    <FieldError id={`${idPrefix}-amount-error`} message={errors.amount} />
                </div>
            </div>
        </Dialog>
    );
}
